import { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import chroma from 'chroma-js';
import { ActionButton } from '../../components/ActionButtons/ActionButtons.style';
import { palette } from '../../theme/colors';
import { ReactComponent as AlarmIcon } from '../../assets/icons/bell-white.svg';
import { useLayoutContext } from '../../utils/customHooks/LayoutContext';

import SoundLwtDispatched from './../../assets/sounds/lwt_dispatched.mp3';

const AUDIO_DELAY_MS = 500;

const dispatchedColorBase = chroma(palette.tartOrange);
const circleColor = dispatchedColorBase.hex();
const ringColor = dispatchedColorBase.alpha(0.4).css();
const gradColor = dispatchedColorBase.set('hsl.s', '+0.3').hex();

type LoneWorkerTimerDispatchedProps = {
  onOpenMessagePress: () => void;
  isLoading: boolean;
};

export const LoneWorkerTimerDispatched = ({
  onOpenMessagePress,
  isLoading,
}: LoneWorkerTimerDispatchedProps) => {
  const layout = useLayoutContext();
  const { t } = useTranslation();

  useEffect(() => {
    layout.setShowHeader(false);
    return () => layout.setShowHeader(true);
  }, [layout]);

  useEffect(() => {
    const audio = new Audio();
    audio.volume = 0;
    audio.loop = true;
    audio.src = SoundLwtDispatched;

    const playPromise = audio.play().catch(() => console.warn('Playback is restricted.'));
    const delayTimeout = setTimeout(() => (audio.volume = 1), AUDIO_DELAY_MS);

    return () => {
      clearTimeout(delayTimeout);
      playPromise.then(() => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  return (
    <SPage>
      <SContent>
        <SCircleContainer>
          <SGlowSvg width="400" height="400" aria-hidden="true">
            <defs>
              <radialGradient id="lwt-dispatched-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={gradColor} stopOpacity="0.32" />
                <stop offset="100%" stopColor={gradColor} stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="200" cy="200" r="160" fill="url(#lwt-dispatched-glow)" />
          </SGlowSvg>
          <SRing $delay={0} />
          <SRing $delay={800} />
          <SRing $delay={1600} />
          <SCircle>
            <AlarmIcon width={36} height={36} />
          </SCircle>
        </SCircleContainer>
        <STextContainer>
          <STitle>{t('work_alone_alarm_dispatched_title')}</STitle>
          <SDescription>{t('work_alone_alarm_dispatched_desc')}</SDescription>
        </STextContainer>
      </SContent>
      <SButtonContainer>
        <ActionButton
          tx="alarm_open_alarm_message"
          onClick={onOpenMessagePress}
          disabled={isLoading}
          style={{ opacity: isLoading ? 0.5 : 1 }}
        />
      </SButtonContainer>
    </SPage>
  );
};

const ringPulse = keyframes`
  0%   { transform: scale(1); opacity: 1; }
  50%  { opacity: 1; }
  100% { transform: scale(3); opacity: 0; }
`;

const SPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
`;

const SContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
`;

const SCircleContainer = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SGlowSvg = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const SRing = styled.div<{ $delay: number }>`
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px solid ${ringColor};
  animation: ${ringPulse} 2.4s ease-out infinite;
  animation-delay: ${props => props.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SCircle = styled.div`
  position: relative;
  z-index: 1;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${circleColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const STextContainer = styled.div`
  margin-top: 32px;
  text-align: center;
  padding: 0 16px;
`;

const STitle = styled.h2`
  color: ${palette.white};
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  margin-bottom: 8px;
`;

const SDescription = styled.p`
  color: ${palette.grayx11gray};
  font-size: 14px;
  line-height: 20px;
`;

const SButtonContainer = styled.div`
  width: 100%;
  padding: 24px 16px 40px;
`;
